<template>
  <div class="column content-box">
    <div class="table-actions-wrap">
      <search
        :search-query="searchQuery"
        :on-input-search="onInputSearch"
        @on-search-submit="getClosingReasons"
      />

      <div class="right-aligned-wrap">
        <woot-button
          color-scheme="success"
          icon="chat-off"
          @click="openAddPopup"
        >
          {{ $t('CLOSING_REASONS.HEADER') }}
        </woot-button>
      </div>
    </div>

    <div class="medium-12">
      <table-content
        :items="closingReasons"
        :show-search-empty-state="showEmptySearchResult"
        :is-loading="uiFlags.isFetching"
        @on-sort-change="onSortChange"
        @on-delete-click="openDeletePopup"
      />

      <table-footer
        :current-page="Number(meta.current_page)"
        :total-count="meta.count"
        :page-size="15"
        @page-change="onPageChange"
      />
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup" size="low">
      <create @on-created="onCreateReason" @on-close="hideAddPopup" />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CLOSING_REASONS.DELETE.CONFIRM.TITLE')"
      :message="$t('CLOSING_REASONS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('CLOSING_REASONS.DELETE.CONFIRM.YES')"
      :reject-text="$t('CLOSING_REASONS.DELETE.CONFIRM.NO')"
      :is-loading="uiFlags.isDeleting"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import alertMixin from 'shared/mixins/alertMixin';

import Search from './Search';
import Create from './Create';
import TableContent from './List';
import TableFooter from 'dashboard/components/widgets/TableFooter';

export default {
  components: {
    Search,
    TableContent,
    TableFooter,
    Create,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      page: 1,
      searchQuery: '',
      oldSearchQuery: '',
      sortConfig: { name: 'desc' },
      showAddPopup: false,
      selected: {},
      showDeleteConfirmationPopup: false,
    };
  },
  computed: {
    ...mapGetters({
      meta: 'closingReasons/getMeta',
      closingReasons: 'closingReasons/getClosingReasons',
      uiFlags: 'closingReasons/getUIFlags',
    }),
    showEmptySearchResult() {
      return !!this.searchQuery && this.closingReasons.length === 0;
    },
  },
  methods: {
    ...mapActions('closingReasons', ['get', 'search', 'delete']),
    async getClosingReasons() {
      try {
        let value = '';

        if (this.searchQuery.charAt(0) === '+') {
          value = this.searchQuery.substring(1);
        } else {
          value = this.searchQuery;
        }

        const requestParams = {
          page: this.page,
          sortAttr: this.getSortAttribute(),
        };

        if (value) {
          if (this.oldSearchQuery && this.searchQuery !== this.oldSearchQuery) {
            this.page = 1;
            requestParams.page = this.page;
          }

          this.oldSearchQuery = this.searchQuery;
          requestParams.search = encodeURIComponent(value.toLowerCase());
          await this.search(requestParams);
        } else {
          await this.get(requestParams);
        }
      } catch (error) {
        this.showAlert(error.message);
      }
    },
    getSortAttribute() {
      let sortAttr = Object.keys(this.sortConfig).reduce((acc, sortKey) => {
        const sortOrder = this.sortConfig[sortKey];
        if (sortOrder) {
          const sortOrderSign = sortOrder === 'asc' ? '' : '-';
          return `${sortOrderSign}${sortKey}`;
        }
        return acc;
      }, '');

      if (!sortAttr) {
        this.sortConfig = { name: 'desc' };
        sortAttr = '-name';
      }

      return sortAttr;
    },
    async deleteClosingReason(id) {
      try {
        await this.delete(id);

        this.showAlert(this.$t('CLOSING_REASONS.DELETE.API.SUCCESS_MESSAGE'));

        this.clearData();
        this.getClosingReasons();
      } catch (error) {
        this.showAlert(this.$t('CLOSING_REASONS.DELETE.API.ERROR_MESSAGE'));
      } finally {
        this.closeDeletePopup();
      }
    },
    onCreateReason() {
      this.clearData();
      this.getClosingReasons();
    },
    onPageChange(page) {
      this.page = Number(page);

      this.getClosingReasons();
    },
    onInputSearch(event) {
      const newQuery = event.target.value;
      const refetchAllClosingReasons = !!this.searchQuery && newQuery === '';
      this.searchQuery = newQuery;

      if (refetchAllClosingReasons) {
        this.getClosingReasons();
      }
    },
    onSortChange(params) {
      console.log(params);
      this.sortConfig = params;

      this.page = 1;
      this.getClosingReasons();
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selected = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      const { id } = this.selected;

      this.deleteClosingReason(id);
    },
    clearData() {
      this.page = 1;
      this.searchQuery = '';
      this.oldSearchQuery = '';
      this.sortConfig = 'id';
    },
  },
  mounted() {
    this.getClosingReasons();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .modal-container {
    .columns.wait-message {
      p {
        margin-top: var(--space-large);
        margin-bottom: 0 !important;
      }

      ~ .modal-footer {
        button {
          opacity: 0.5;
          pointer-events: none;
        }
      }
    }
  }
}

.table-actions-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>